import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { LandingLayout } from '@/pages/landing/shared/LandingLayout';
import { SEOHead } from '@/pages/landing/shared/SEOHead';
import { generateBreadcrumbSchema, generateCanonicalUrl, combineSchemas } from '@/utils/seo';
import { getBlogPosts } from '@/content/blog/loader';

export default function BlogIndex() {
  const { t, i18n } = useTranslation(['blog', 'common']);
  const posts = getBlogPosts(i18n.language);

  const structuredData = combineSchemas(
    generateBreadcrumbSchema([
      { name: 'Home', url: 'https://slotyapp.com' },
      { name: t('blog:title'), url: 'https://slotyapp.com/blog' },
    ])
  );

  return (
    <LandingLayout>
      <SEOHead
        title={t('blog:seo.title')}
        description={t('blog:seo.description')}
        keywords={t('blog:seo.keywords')}
        canonical={generateCanonicalUrl('/blog')}
        ogType="website"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-brand-50 to-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display">
            {t('blog:title')}
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            {t('blog:subtitle')}
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {posts.length === 0 ? (
          <p className="text-center text-slate-500">{t('blog:noPosts')}</p>
        ) : (
          <div className="space-y-8">
            {posts.map((post) => (
              <article
                key={post.meta.slug}
                className="group border border-slate-200 rounded-xl p-6 hover:border-brand-300 hover:shadow-md transition-all"
              >
                <Link href={`/blog/${post.meta.slug}`}>
                  <div className="flex flex-col gap-3">
                    <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 group-hover:text-brand-600 transition-colors">
                      {post.meta.title}
                    </h2>
                    <p className="text-slate-600 line-clamp-2">
                      {post.meta.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(post.meta.date).toLocaleDateString(
                          i18n.language === 'de' ? 'de-DE' : i18n.language === 'pt-BR' ? 'pt-BR' : i18n.language === 'pt-PT' ? 'pt-PT' : 'en-US',
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        )}
                      </span>
                      {post.meta.readingTime && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {t('blog:readingTime', { minutes: post.meta.readingTime })}
                        </span>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-700">
                      {t('blog:readMore')} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>
    </LandingLayout>
  );
}
